import { useMemo } from 'react';

export type UserRole = 'admin' | 'supervisor' | 'agent' | 'analyst';
type BackendRole =
  | UserRole
  | 'SUPER_ADMIN'
  | 'CNS_ADMIN'
  | 'CNS_OFFICER'
  | 'CNS_SUPERVISOR'
  | 'CNS_ANALYST';

export interface User {
  id: string;
  email: string;
  name?: string;
  role: BackendRole | string;
}

export interface PermissionConfig {
  roles: UserRole[];
}

// Définition des permissions par fonctionnalité
export const PERMISSIONS = {
  // Gestion des hôtels
  HOTELS_VIEW: { roles: ['admin', 'supervisor', 'analyst'] as UserRole[] },
  HOTELS_CREATE: { roles: ['admin', 'supervisor'] as UserRole[] },
  HOTELS_EDIT: { roles: ['admin', 'supervisor'] as UserRole[] },
  HOTELS_DELETE: { roles: ['admin'] as UserRole[] },
  HOTELS_SUSPEND: { roles: ['admin', 'supervisor'] as UserRole[] },

  // Gestion des utilisateurs
  USERS_VIEW: { roles: ['admin'] as UserRole[] },
  USERS_CREATE: { roles: ['admin'] as UserRole[] },
  USERS_EDIT: { roles: ['admin'] as UserRole[] },
  USERS_DELETE: { roles: ['admin'] as UserRole[] },
  USERS_RESET_PASSWORD: { roles: ['admin'] as UserRole[] },
  USERS_TOGGLE_STATUS: { roles: ['admin'] as UserRole[] },

  // Configuration des taxes
  TAX_VIEW: { roles: ['admin', 'supervisor', 'analyst'] as UserRole[] },
  TAX_EDIT: { roles: ['admin', 'supervisor'] as UserRole[] },

  // Paramètres système
  SETTINGS_VIEW: { roles: ['admin'] as UserRole[] },
  SETTINGS_EDIT: { roles: ['admin'] as UserRole[] },

  // Dashboard opérationnel
  OPERATIONAL_DASHBOARD: { roles: ['admin', 'supervisor', 'analyst'] as UserRole[] },

  // Alertes de sécurité
  ALERTS_VIEW: { roles: ['admin', 'supervisor', 'agent', 'analyst'] as UserRole[] },
  ALERTS_ACKNOWLEDGE: { roles: ['admin', 'supervisor', 'agent'] as UserRole[] },
  ALERTS_DISMISS: { roles: ['admin', 'supervisor'] as UserRole[] },

  // Règles de scoring
  SCORING_RULES_VIEW: { roles: ['admin', 'supervisor', 'analyst'] as UserRole[] },
  SCORING_RULES_EDIT: { roles: ['admin', 'supervisor'] as UserRole[] },

  // Scénarios de risque
  SCENARIOS_VIEW: { roles: ['admin', 'supervisor', 'analyst'] as UserRole[] },
  SCENARIOS_EDIT: { roles: ['admin', 'supervisor'] as UserRole[] },

  // Liste de surveillance
  WATCHLIST_VIEW: { roles: ['admin', 'supervisor', 'agent', 'analyst'] as UserRole[] },
  WATCHLIST_EDIT: { roles: ['admin', 'supervisor'] as UserRole[] },

  // Statistiques
  STATISTICS_VIEW: { roles: ['admin', 'supervisor', 'analyst'] as UserRole[] },
  STATISTICS_EXPORT: { roles: ['admin', 'supervisor', 'analyst'] as UserRole[] },

  // Messages
  MESSAGES_VIEW: { roles: ['admin', 'supervisor', 'agent'] as UserRole[] },
  MESSAGES_SEND: { roles: ['admin', 'supervisor', 'agent'] as UserRole[] },

  // Invités
  GUESTS_VIEW: { roles: ['admin', 'supervisor', 'agent', 'analyst'] as UserRole[] },
  GUESTS_EDIT: { roles: ['admin', 'supervisor', 'agent'] as UserRole[] },

  // Documents
  DOCUMENTS_VIEW: { roles: ['admin', 'supervisor', 'agent', 'analyst'] as UserRole[] },
  DOCUMENTS_VALIDATE: { roles: ['admin', 'supervisor', 'agent'] as UserRole[] },

  // Audit Logs
  AUDIT_VIEW: { roles: ['admin'] as UserRole[] },

  // Health Monitoring
  HEALTH_VIEW: { roles: ['admin', 'supervisor'] as UserRole[] },

  // Regulations
  REGULATIONS_VIEW: { roles: ['admin', 'supervisor', 'analyst'] as UserRole[] },
  REGULATIONS_EDIT: { roles: ['admin', 'supervisor'] as UserRole[] },

  // Relationship Analysis
  RELATIONSHIPS_VIEW: { roles: ['admin', 'supervisor', 'analyst'] as UserRole[] },

  // Investigation Module
  INVESTIGATION_SEARCH: { roles: ['admin', 'supervisor', 'agent', 'analyst'] as UserRole[] },
  INVESTIGATION_PERSON_READ: { roles: ['admin', 'supervisor', 'agent', 'analyst'] as UserRole[] },
  INVESTIGATION_GEO_READ: { roles: ['admin', 'supervisor', 'agent', 'analyst'] as UserRole[] },
  INVESTIGATION_MATCH_READ: { roles: ['admin', 'supervisor', 'analyst'] as UserRole[] },
  INVESTIGATION_MATCH_RESOLVE: { roles: ['admin', 'supervisor'] as UserRole[] },
  INVESTIGATION_ADMIN: { roles: ['admin'] as UserRole[] },
} as const;

export type Permission = keyof typeof PERMISSIONS;

export function normalizeUserRole(role?: string | null): UserRole {
  switch ((role || '').trim().toUpperCase()) {
    case 'SUPER_ADMIN':
    case 'CNS_ADMIN':
    case 'CNS_OFFICER':
    case 'ANR_ADMIN':
    case 'ANR_OFFICER':
    case 'ADMIN':
      return 'admin';
    case 'CNS_SUPERVISOR':
    case 'ANR_SUPERVISOR':
    case 'SUPERVISOR':
      return 'supervisor';
    case 'CNS_ANALYST':
    case 'ANR_ANALYST':
    case 'ANALYST':
      return 'analyst';
    case 'AGENT':
    default:
      return 'agent';
  }
}

/**
 * Hook pour gérer les permissions utilisateur
 */
export function usePermissions() {
  const user = useMemo(() => {
    const userStr = localStorage.getItem('user');
    if (!userStr) return null;
    try {
      return JSON.parse(userStr) as User;
    } catch {
      return null;
    }
  }, []);

  const userRole = normalizeUserRole(user?.role);

  /**
   * Vérifie si l'utilisateur a une permission spécifique
   */
  const hasPermission = (permission: Permission): boolean => {
    const config = PERMISSIONS[permission];
    return config.roles.includes(userRole);
  };

  /**
   * Vérifie si l'utilisateur a au moins une des permissions données
   */
  const hasAnyPermission = (...permissions: Permission[]): boolean => {
    return permissions.some(permission => hasPermission(permission));
  };

  /**
   * Vérifie si l'utilisateur a toutes les permissions données
   */
  const hasAllPermissions = (...permissions: Permission[]): boolean => {
    return permissions.every(permission => hasPermission(permission));
  };

  /**
   * Vérifie si l'utilisateur a un des rôles donnés
   */
  const hasRole = (...roles: UserRole[]): boolean => {
    return roles.includes(userRole);
  };

  /**
   * Vérifie si l'utilisateur est admin
   */
  const isAdmin = userRole === 'admin';

  /**
   * Vérifie si l'utilisateur est superviseur ou admin
   */
  const isSupervisorOrAdmin = userRole === 'admin' || userRole === 'supervisor';

  return {
    user,
    userRole,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    isAdmin,
    isSupervisorOrAdmin,
  };
}
